import { checkIsBusinessUser } from "selectors/user";
import {
  checkSelectedBusinessRpaMode,
  getCurrentQuickBooksRealmId,
  getSelectedBusinessBookCloseDate,
  getSelectedBusinessData,
  getSelectedBusinessIntegrationServiceData
} from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import CommentTargetTypes from "const/CommentTargetTypes";
import CommentTypes from "const/CommentTypes";
import CommentsActions from "actions/CommentsActions";
import DataStatuses from "const/DataStatuses";
import DateTimeFormats from "const/DateTimeFormats";
import Objects from "utils/Objects";
import Texts from "utils/Texts";
import TransactionFieldsToEdit from "const/TransactionFieldsToEdit";
import Transactions from "utils/Transactions";
import TransactionsActions from "actions/TransactionsActions";
import moment from "moment";
import useShowCommonModal from "./useShowCommonModal";

const useTransactionUtils = () => {
  const dispatch = useDispatch();

  const { uiTexts, messages } = useSelector(getTextsData);

  const {
    settings: {
      advancedDocumentsWorkflow,
      allowApproveWithoutPayee
    } = {}
  } = useSelector(getSelectedBusinessData);

  const businessUser = useSelector(checkIsBusinessUser);

  const quickBooksBusiness = !!useSelector(getCurrentQuickBooksRealmId);

  const selectedBusinessBookCloseDate = useSelector(getSelectedBusinessBookCloseDate);

  const selectedBusinessIntegrationServiceData = useSelector(getSelectedBusinessIntegrationServiceData);

  const rpaMode = useSelector(checkSelectedBusinessRpaMode);

  const showCommonModal = useShowCommonModal();

  const checkIsReadyToReview = useCallback((state, data) => {
    const { reason, category = {} } = state || {};

    const { status, documentId, attachmentId } = data || {};

    return businessUser
      && status === DataStatuses.NEED_REACTION
      && (documentId || attachmentId || reason.trim() || category.code);
  }, [businessUser]);

  const checkIsReadyToApprove = useCallback((transactionState, transaction) => {
    if (!Transactions.validateContactChange(transactionState, transaction)) return false;

    const { status, documentStatus, documentId } = transaction;

    if (
      rpaMode
      && [DataStatuses.NEED_DOCUMENT, DataStatuses.TO_REVIEW].includes(status)
      && documentStatus === DataStatuses.EXPORTED
    ) {
      return true;
    }

    return !businessUser
        && (!advancedDocumentsWorkflow || !documentId)
        && status !== DataStatuses.TO_REPORT
        && status !== DataStatuses.EXPORTED
        && Transactions.validateToApprove(transactionState, transaction, allowApproveWithoutPayee);
  }, [advancedDocumentsWorkflow, businessUser, allowApproveWithoutPayee, rpaMode]);

  const editTransaction = useCallback(async(transactionId, transactionState) => {
    const updateData = Objects.getProps(transactionState, Object.values(TransactionFieldsToEdit));

    const { address, vendorId, manualMode } = transactionState;

    const reason = (transactionState.reason || "").trim();

    const result = await dispatch(TransactionsActions.editTransaction(
      transactionId,
      !businessUser && rpaMode && reason && !manualMode
        ? { reason, address, vendorId }
        : updateData
    ));

    if (reason) {
      dispatch(CommentsActions.fetchComments(CommentTargetTypes.TRANSACTIONS, transactionId, false, true)).then(() => {
        dispatch(CommentsActions.addNewComment(CommentTypes.COMMENT, `${uiTexts.reason}: ${reason}`, [], true));
      });
    }

    return result;
  }, [dispatch, businessUser, rpaMode, uiTexts.reason]);

  const onTransactionApplyChanges = useCallback((transaction, transactionState) => {
    const categorizeByReason = rpaMode && transactionState.reason?.trim() && !transactionState.manualMode;

    const contactChangeValid = businessUser || categorizeByReason
      || Transactions.validateContactChange(transactionState, transaction);

    if (!contactChangeValid) return false;

    const { id: transactionId, status, timestamp } = transaction;

    const exportedStatus = status === DataStatuses.EXPORTED && !quickBooksBusiness;

    const closedBookTransaction = selectedBusinessBookCloseDate
      && moment.utc(timestamp).isSameOrBefore(moment.utc(selectedBusinessBookCloseDate));

    const warning = !exportedStatus && closedBookTransaction;

    if (warning) {
      const closedBookTimeText = moment.utc(selectedBusinessBookCloseDate).format(DateTimeFormats.DATE_TEXT_EXT);

      showCommonModal({
        text: Texts.replaceVars(messages.closedBookWarning, {
          date: closedBookTimeText,
          service: selectedBusinessIntegrationServiceData.label
        }),
        headerText: uiTexts.warning
      });

      return false;
    }

    return editTransaction(transactionId, transactionState);
  }, [
    businessUser,
    editTransaction,
    messages,
    quickBooksBusiness,
    rpaMode,
    selectedBusinessBookCloseDate,
    selectedBusinessIntegrationServiceData?.label,
    showCommonModal,
    uiTexts
  ]);

  return {
    checkIsReadyToReview,
    checkIsReadyToApprove,
    onTransactionApplyChanges
  };
};

export default useTransactionUtils;
